import type { Task } from "@app/core";
import { Check } from "@app/ui";
import { useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { getDragTaskId, setDragTaskId, setTaskDragging } from "./drag.js";
import { useI18n } from "../../lib/useI18n.js";
import { useCalendarChildInfo } from "./CalendarRenderIndex.js";

/** The undated subtasks rendered INSIDE a parent's calendar block. Each row:
 *  click → edit; drag onto the grid → schedule (detach); drag onto a sibling row
 *  → reorder. A subtask that itself has subtasks shows a `done/total` badge, and
 *  — only when the block is hover-`expanded` (so there's guaranteed space and no
 *  other content gets pushed out) — expands to list its own sub-subtasks.
 *  `accent` is a slightly-brighter shade of the parent's color. */
export function SubtaskRows({ parentId, children, accent, expanded }: {
  parentId: string;
  children: Task[];
  accent?: string | undefined;
  expanded?: boolean;
}) {
  if (children.length === 0) return null;
  return (
    <div className="mt-0.5 flex flex-col gap-px">
      {children.map((c) => (
        <SubtaskRow key={c.id} task={c} accent={accent} expanded={!!expanded} depth={0} />
      ))}
    </div>
  );
}

function SubtaskRow({ task, accent, expanded, depth }: {
  task: Task;
  accent?: string | undefined;
  expanded: boolean;
  depth: number;
}) {
  const { t } = useI18n();
  const { children, progress } = useCalendarChildInfo(task.id);
  const complete = useTasksStore((s) => s.complete);
  const reorderSubtask = useTasksStore((s) => s.reorderSubtask);
  const openEdit = useTaskDialogStore((s) => s.openEdit);

  const done = task.status === "done";
  const grandkids = children.filter((k) => !k.due_at);

  return (
    <>
      <div
        draggable
        data-subtask-id={task.id}
        onDragStart={(e) => { e.stopPropagation(); setDragTaskId(e, task.id); setTaskDragging(true); }}
        onDragEnd={() => setTaskDragging(false)}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          const dropped = getDragTaskId(e);
          if (!dropped || dropped === task.id) return;
          const src = useTasksStore.getState().tasks.find((t) => t.id === dropped);
          if (!src || src.subtask_parent_id !== task.subtask_parent_id) return; // only a sibling reorder
          e.preventDefault();
          e.stopPropagation();
          void reorderSubtask(task.subtask_parent_id!, dropped, task.id);
        }}
        onClick={(e) => { e.stopPropagation(); openEdit(task); }}
        style={{ marginLeft: depth * 12, ...(accent ? { backgroundColor: accent } : {}) }}
        className={`group/sub flex cursor-pointer items-center gap-1 rounded-sm px-1 text-[calc(10px*var(--app-text-scale))] leading-tight transition-colors ${accent ? "" : "bg-black/5 dark:bg-white/10"} hover:brightness-110 hover:ring-1 hover:ring-current/30`}
        title={t("calendar.subtaskRowHint")}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void complete(task.id, !done); }}
          aria-label={done ? "Mark incomplete" : "Mark complete"}
          className={`group/btn flex h-3 w-3 shrink-0 items-center justify-center rounded-full border border-current/50 transition-colors ${done ? "bg-current/20" : "bg-transparent hover:bg-current/10"}`}
        >
          <Check className={`h-2 w-2 transition-opacity duration-150 ${done ? "opacity-100" : "opacity-0 group-hover/btn:opacity-40"}`} />
        </button>
        <span className={`truncate py-0.5 ${done ? "line-through opacity-60" : ""}`}>
          {task.title || "(untitled)"}
        </span>
        {progress.total > 0 && (
          <span className="ml-0.5 shrink-0 rounded bg-black/10 px-1 text-[calc(9px*var(--app-text-scale))] tabular-nums dark:bg-white/15">
            {progress.done}/{progress.total}
          </span>
        )}
      </div>
      {expanded && grandkids.map((g) => (
        <SubtaskRow key={g.id} task={g} accent={accent} expanded={expanded} depth={depth + 1} />
      ))}
    </>
  );
}
